import { IMAGENES_DEL_EXPEDIENTE, type TipoDeImagenDelInforme } from "@/lib/informes";

import type { EntradaDelIndice, PendienteDelIndice } from "./IndiceDeLaPestana";

/**
 * Lo que el índice y los pendientes de "Descripción de la propiedad" saben de
 * la pestaña.
 *
 * Es PURO y recibe lo que hay en pantalla —el texto y la casilla que la persona
 * está escribiendo, no lo guardado—, así que la columna de la derecha se mueve
 * con cada tecla, igual que la de Antecedentes generales.
 */

export type ClaveDeDescripcion = "limites" | "generales" | "vias" | "alejada" | "notas";

export const SECCIONES_DE_DESCRIPCION: { clave: ClaveDeDescripcion; n: string; etiqueta: string }[] = [
  { clave: "limites", n: "01", etiqueta: "Límites de cada rol" },
  { clave: "generales", n: "02", etiqueta: "Límites generales" },
  { clave: "vias", n: "03", etiqueta: "Vías de acceso" },
  { clave: "alejada", n: "04", etiqueta: "Vista alejada" },
  { clave: "notas", n: "05", etiqueta: "Notas u observaciones" },
];

/** Qué tarjeta dibuja cada encuadre fijo. */
export const SECCION_DEL_ENCUADRE: Record<
  Extract<TipoDeImagenDelInforme, "limites_generales" | "vias_acceso" | "vista_alejada">,
  ClaveDeDescripcion
> = {
  limites_generales: "generales",
  vias_acceso: "vias",
  vista_alejada: "alejada",
};

/** El `id` del DOM de cada tarjeta numerada: a donde lleva el índice. */
export function idDeLaSeccionDeDescripcion(clave: ClaveDeDescripcion): string {
  return `descripcion-${clave}`;
}

/** El `id` del DOM del bloque de un rol. */
export function idDelRolDeDescripcion(rolId: number): string {
  return `descripcion-rol-${rolId}`;
}

export interface RolParaElIndice {
  id: number;
  rol_sii: string;
  comuna: string | null;
  /** Lo que hay escrito AHORA en el textarea. */
  texto: string;
  validada: boolean;
  hayImagen: boolean;
}

export interface EntradaDeDescripcion {
  roles: RolParaElIndice[];
  imagenes: Record<keyof typeof SECCION_DEL_ENCUADRE, boolean>;
  notas: string;
}

const escrito = (texto: string) => texto.trim() !== "";

/** Un rol está completo con las TRES cosas: texto, revisión de una persona e imagen. */
function avanceDelRol(rol: RolParaElIndice): number {
  return [escrito(rol.texto), rol.validada, rol.hayImagen].filter(Boolean).length;
}

const rolCompleto = (rol: RolParaElIndice) => avanceDelRol(rol) === 3;

export function seccionesDeDescripcionCompletas(
  e: EntradaDeDescripcion,
): Record<ClaveDeDescripcion, boolean> {
  return {
    limites: e.roles.length > 0 && e.roles.every(rolCompleto),
    generales: e.imagenes.limites_generales,
    vias: e.imagenes.vias_acceso,
    alejada: e.imagenes.vista_alejada,
    notas: escrito(e.notas),
  };
}

export function avanceDeDescripcion(completas: Record<ClaveDeDescripcion, boolean>) {
  const total = SECCIONES_DE_DESCRIPCION.length;
  const hechas = SECCIONES_DE_DESCRIPCION.filter(({ clave }) => completas[clave]).length;

  return { hechas, total, porcentaje: (hechas / total) * 100 };
}

function nombreDelRol(rol: RolParaElIndice): string {
  return rol.comuna ? `${rol.rol_sii} · ${rol.comuna}` : rol.rol_sii;
}

/**
 * Lo que falta, en el orden en que se lee la pestaña. LAS NOTAS NUNCA SON UN
 * PENDIENTE: son opcionales.
 *
 * Un texto vacío no pide además la validación: no hay nada que revisar, y dos
 * avisos sobre el mismo rol por la misma causa serían ruido.
 */
export function pendientesDeDescripcion(e: EntradaDeDescripcion): PendienteDelIndice[] {
  const lista: PendienteDelIndice[] = [];

  for (const rol of e.roles) {
    const destino = idDelRolDeDescripcion(rol.id);
    const nombre = `Rol ${nombreDelRol(rol)}`;

    if (!escrito(rol.texto)) {
      lista.push({ destino, texto: `${nombre}: falta la descripción de deslindes.` });
    }
    if (!rol.hayImagen) {
      lista.push({ destino, texto: `${nombre}: falta la imagen del límite.` });
    }
    if (escrito(rol.texto) && !rol.validada) {
      lista.push({ destino, texto: `${nombre}: descripción sin validar.` });
    }
  }

  for (const { tipo, etiqueta } of IMAGENES_DEL_EXPEDIENTE) {
    if (!(tipo in SECCION_DEL_ENCUADRE)) continue;

    if (!e.imagenes[tipo as keyof typeof SECCION_DEL_ENCUADRE]) {
      lista.push({
        destino: idDeLaSeccionDeDescripcion(SECCION_DEL_ENCUADRE[tipo as keyof typeof SECCION_DEL_ENCUADRE]),
        texto: `Falta la imagen de ${etiqueta}.`,
      });
    }
  }

  return lista;
}

/**
 * Las filas del índice: las cinco secciones y, con VARIOS roles, una entrada
 * por rol bajo la de límites. Con uno solo sería repetir la misma fila.
 */
export function entradasDelIndiceDeDescripcion(e: EntradaDeDescripcion): EntradaDelIndice[] {
  const completas = seccionesDeDescripcionCompletas(e);
  const entradas: EntradaDelIndice[] = [];

  for (const { clave, n, etiqueta } of SECCIONES_DE_DESCRIPCION) {
    const cuenta =
      clave === "limites"
        ? `${e.roles.filter(rolCompleto).length}/${e.roles.length}`
        : clave === "notas"
          ? completas.notas
            ? "1/1"
            : "Opc."
          : completas[clave]
            ? "1/1"
            : "0/1";

    entradas.push({
      destino: idDeLaSeccionDeDescripcion(clave),
      n,
      etiqueta,
      completa: completas[clave],
      cuenta,
    });

    if (clave === "limites" && e.roles.length > 1) {
      for (const rol of e.roles) {
        entradas.push({
          destino: idDelRolDeDescripcion(rol.id),
          n: "",
          etiqueta: `Rol ${rol.rol_sii}`,
          completa: rolCompleto(rol),
          cuenta: `${avanceDelRol(rol)}/3`,
          nivel: 1,
        });
      }
    }
  }

  return entradas;
}
